/**
 * ============================================================================
 * CORE SERVICE: Central HTTP Client (Axios Instance)
 * ============================================================================
 * Purpose:
 *   Configures and exports the centralized Axios HTTP client instance.
 *   Automatically attaches Bearer JWT tokens, multi-tenant headers (`x-account-id`,
 *   `x-user-id`, `x-db-name`), and normalizes API error responses.
 * ============================================================================
 */

import axios from "axios";
import { API_BASE_URL, TENANT_HEADERS, STORAGE_KEYS } from "../config/constants";
import { getUserRole } from "../utils/auth";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 20000,
});

/**
 * Request Interceptor:
 * Attaches Authorization Bearer token and active tenant context headers.
 */
api.interceptors.request.use(
  (config) => {
    // 1. Attach JWT Auth Token
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 2. Attach Multi-Tenant Database Context Headers if available
    let activeAccountId = localStorage.getItem(STORAGE_KEYS.ACTIVE_ACCOUNT_ID);
    const activeDbName = localStorage.getItem(STORAGE_KEYS.ACTIVE_DB_NAME);

    // Derive user profile context (role, username, fallback account ID)
    try {
      const storedUser = localStorage.getItem(STORAGE_KEYS.USER || "user");
      if (storedUser) {
        const u = JSON.parse(storedUser);
        const role = getUserRole(u);
        if (role && !config.headers["x-user-role"]) {
          config.headers["x-user-role"] = role;
        }
        if (u.str_username && !config.headers["x-username"]) {
          config.headers["x-username"] = u.str_username;
        }
        if (!activeAccountId) {
          activeAccountId = u.str_account_id || u.int_account_id || (u.int_role_id === 3 ? u.int_user_id : null);
        }
        const userObjId = u._id || u.obj__id || u.int_user_id;
        if (userObjId && !config.headers[TENANT_HEADERS.USER_ID]) {
          config.headers[TENANT_HEADERS.USER_ID] = String(userObjId);
        }
        if (userObjId && !activeDbName && !config.headers[TENANT_HEADERS.DB_NAME] && role === "user") {
          config.headers[TENANT_HEADERS.DB_NAME] = `user_${userObjId}`;
        }
      }
    } catch (_) {}

    if (activeAccountId && !config.headers[TENANT_HEADERS.ACCOUNT_ID]) {
      config.headers[TENANT_HEADERS.ACCOUNT_ID] = String(activeAccountId);
    }

    if (activeDbName && !config.headers[TENANT_HEADERS.DB_NAME]) {
      config.headers[TENANT_HEADERS.DB_NAME] = activeDbName;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response Interceptor:
 * Normalizes error messages and extracts response body data.
 */
api.interceptors.response.use(
  (response) => {
    const data = response.data;
    if (data && typeof data === "object") {
      if (!data.pagination && data.meta) {
        data.pagination = data.meta;
      }
    }
    return data;
  },
  (error) => {
    const message =
      (Array.isArray(error.response?.data?.details) && error.response.data.details.join(", ")) ||
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "An unexpected server error occurred";

    // Handle 401 Unauthorized globally if token has expired
    if (error.response?.status === 401 && !window.location.pathname.includes("/signin")) {
      console.warn("⚠️ Session expired or unauthorized, redirecting to login...");
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
    }

    return Promise.reject(new Error(message));
  }
);

export default api;
